import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { useSession } from "users/__session/session-context";
import WorkAgreement, { WorkAgreementFieldCategory } from "work/work-agreements/work-agreement";
import downloadFile from "_shared/_utilities/download-file";
import DocumentAPIService from "documents/_api/document-api-service";
import DocumentListReadonly from "work/work-agreements/contracts/_views/document-list-readonly";
import { WorkDocumentType } from "work/work-agreements/contracts/types";
import { Masked } from "work/_shared/masked-fields";

const ListContainer = styled(
  Grid,
  {
    shouldForwardProp: (prop) => prop !== "waived",
  }
)<{ waived: boolean }>(
  ({ waived }) => ({
    flexWrap: "nowrap",
    minHeight: "20rem",
    opacity: waived ? 0.25 : 1,
    pointerEvents: waived ? "none" : "auto",
    width: "100%",
  }));
const DocumentList = styled(DocumentListReadonly)(
  ({ theme }) => ({
    [theme.breakpoints.down("lg")]: {
      marginTop: theme.spacing(2),
    },
    maxHeight: "60vh",
    marginLeft: theme.spacing(2),
    overflowY: "auto",
  }));
const HeadControls = styled("div")(
  ({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    paddingLeft: theme.spacing(2),
  }));
const HelperText = styled(Typography)(
  ({ theme }) => ({
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  }));

type ConflictsTabProps = {
  activeTab: WorkAgreementFieldCategory;
  workAgreement: WorkAgreement;
};

export default function ConflictsTab(props: Readonly<ConflictsTabProps>) {
  const {
    workAgreement,
    activeTab
  } = props;

  const [isDownloadingFile, setIsDownloadingFile] = React.useState<Guid | null>(null);

  const session = useSession();

  async function handleDownloadDocumentById(
    documentId: Guid,
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): Promise<void> {
    event?.stopPropagation();
    try {
      setIsDownloadingFile(documentId);
      const documentApiService = new DocumentAPIService(session);
      const conflictsDocument = await documentApiService.downloadDocument(
        documentId
      );
      await downloadFile(conflictsDocument);
    } catch (err) {
      console.error(err);
      enqueueSnackbar(
        "Failed to download document. Please try again later.",
        { variant: "error" }
      );
    } finally {
      setIsDownloadingFile(null);
    }
  }

  if (activeTab !== WorkAgreementFieldCategory.Conflicts) return null;
  if (!workAgreement) return null;
  if (Masked.isMasked(workAgreement.conflictsCheckWaived) ||
    Masked.isMasked(workAgreement.conflicts)) {
    return "You are not permitted to view contracts."
  }

  return (
    <>
      <HeadControls>
        <FormControlLabel
          control={
            <Switch
              disabled={true}
              checked={workAgreement.conflictsCheckWaived.value ?? false}
              color="primary"
            />
          }
          label={
            workAgreement.conflictsCheckWaived.value ? (
              <strong>Conflicts Check Waived</strong>
            ) : (
              "Conflicts Check Not Waived"
            )
          }
        />
      </HeadControls>
      <ListContainer
        waived={workAgreement.conflictsCheckWaived.value ?? false}
        container
        direction="row"
      >
        <Grid size={4}>
          {workAgreement.conflicts.length === 0 &&
            <HelperText>
              No conflicts documents for this work agreement.
            </HelperText>
          }{workAgreement.conflicts.length > 0 &&
            <DocumentList
              documents={workAgreement.conflicts ?? []}
              documentType={WorkDocumentType.Conflicts}
              downloadingFileId={isDownloadingFile}
              disableCommentsPane={true}
              onDownload={handleDownloadDocumentById}
            />}
        </Grid>
      </ListContainer>
    </>
  );
}
